import { isProduction } from '@upright/common/environment';
import { forceEnableGTM, useIsGoogleTagManagerEnabled } from '../utils';
import { flattenObjectWithSeparator } from '@upright/common/utils/flatten-object';
import { GoogleTagManagerEventContext, useGoogleTagManagerEventContext } from './gtm-event-context';
import { CustomGtmEventPayload, isConversionEventType, SerializedPayload } from './payloads';

/**
 * Facade does not support user authentication, so we can't use useIsGoogleTagManagerEnabled. The
 * downside is that any facade clicks include team users.
 */
export function usePushAnonymousGtmEvent() {
  const shouldPush = isProduction();
  return _usePushGtmEvent(shouldPush || forceEnableGTM);
}

export function usePushGtmEvent() {
  const shouldPush = useIsGoogleTagManagerEnabled();
  return _usePushGtmEvent(shouldPush);
}

function _usePushGtmEvent(enabled: boolean) {
  const context = useGoogleTagManagerEventContext();
  return (payload: CustomGtmEventPayload) => {
    if (!enabled) {
      return;
    }
    try {
      if (!window.dataLayer) {
        throw Error(`dataLayer has not been initialized. Payload ${JSON.stringify(payload, undefined, 2)}.`);
      }
      /**
       * GTM is disabled in development environments to ensure we don't pollute usage stats with
       * developer generated events. When adding new calls to the hook, it's a good idea to test they
       * fire as expected with a e.g., console.log call (console.log(payload))
       */
      const serializedPayload = serializePayload(payload, context);
      window.dataLayer.push(serializedPayload);
    } catch (error) {
      console.error('Failed pushing GTM event', error);
    }
    // pushGoogleAnalyticsEvent(payload);
  };
}

function serializePayload(payload: CustomGtmEventPayload, context: GoogleTagManagerEventContext): SerializedPayload {
  const resolvedContext = payload.contextOverride ? { ...context, ...payload.contextOverride } : context;
  const data = 'data' in payload ? payload.data : undefined;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const flattenedParams = flattenObjectWithSeparator(
    {
      upright: {
        data,
        context,
      },
    },
    '_',
  );

  if (data) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return {
      event: payload.event,
      data: JSON.stringify(data),
      context: JSON.stringify(resolvedContext),
      upright_is_conversion: isConversionEventType(payload.event),
      ...flattenedParams,
    };
  } else {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return {
      event: payload.event,
      context: JSON.stringify(resolvedContext),
      upright_is_conversion: isConversionEventType(payload.event),
      ...flattenedParams,
    };
  }
}
